import { cn } from "@/libraries/cn";
import type {
	TextFieldProps,
	TextFieldVariants,
} from "@mui/material/TextField";
import TextField from "@mui/material/TextField";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { useMemo } from "react";
import { Typography } from "../text";

type InputTextAreaProps<Variant extends TextFieldVariants = TextFieldVariants> =
	Omit<TextFieldProps<Variant>, "multiline"> & {
		maxLengthClassName?: string;
		maxLength?: number;
		value?: string;
	};

const theme = createTheme({
	components: {
		MuiOutlinedInput: {
			styleOverrides: {
				root: {
					borderRadius: "12px",
					".MuiOutlinedInput-notchedOutline": {
						borderColor: "#71717A",
					},
					"&:hover .MuiOutlinedInput-notchedOutline": {
						borderColor: "#71717A",
					},
					"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
						borderColor: "#71717A",
					},
					"&.Mui-error .MuiOutlinedInput-notchedOutline": {
						borderColor: "#DC2626",
					},
				},
				input: {
					color: "#3F3F46",
				},
			},
		},
		MuiInputLabel: {
			styleOverrides: {
				root: {
					maxWidth: "310px",
					fontFamily: "Montserrat",
					fontSize: "16px",
					textWrap: "pretty",
					color: "#3F3F46",
					backgroundColor: "#FFFFFF",
					transition: "transform 200ms ease-in-out",
					"&.Mui-focused": {
						color: "#3F3F46",
					},
					"&.MuiFormLabel-filled": {
						textWrap: "nowrap",
						top: 0,
						maxWidth: "100%",
						transform: "translate(12px, -10px) scale(0.75)", // Filled position
					},
					"&.Mui-error": {
						color: "#DC2626",
					},
				},
			},
		},
		MuiInputAdornment: {
			styleOverrides: {
				root: {
					fontSize: "24px",
					color: "#3F3F46",
				},
			},
		},
		MuiFormHelperText: {
			styleOverrides: {
				root: {
					fontFamily: "Montserrat",
					"&.Mui-error": {
						color: "#DC2626",
					},
				},
			},
		},
	},
});

const InputTextArea = (props: InputTextAreaProps) => {
	const { maxLength, maxLengthClassName, ...textFieldProps } = props;
	const valueLength = useMemo(
		() => textFieldProps.value?.length || 0,
		[textFieldProps.value],
	);

	return (
		<ThemeProvider theme={theme}>
			<div className="relative w-full h-fit">
				<TextField multiline {...textFieldProps} />
				{maxLength ? (
					<Typography
						fontSize={12}
						fontWeight={400}
						color={textFieldProps.error ? "error" : undefined}
						className={cn(
							"absolute right-0 px-4 py-3",
							{
								"bottom-0": !props.error,
								"bottom-5": props.error,
							},
							maxLengthClassName,
						)}
					>
						{valueLength}/{maxLength}
					</Typography>
				) : null}
			</div>
		</ThemeProvider>
	);
};

export default InputTextArea;
